import React from 'react';
import { useTranslation } from 'next-i18next';
import useOnclickOutside from 'react-cool-onclickoutside';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Button, Icon } from '@components/ui';
import { KEY_CODES } from '@constants/keycodes';
import * as ProductHelpers from '@helpers/ProductHelpers';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useConfig } from '@hooks/data/useConfig';

import styles from './ProductAddToCart.module.scss';

const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
};

const ALIGNMENTS = {
  START: 'start',
  END: 'end'
};

const ADD_TO_CART_ICON_SIZES = {
  [SIZES.SMALL]: 20,
  [SIZES.MEDIUM]: 22,
  [SIZES.LARGE]: 24,
};

const QUANTITY_ICON_SIZES = {
  [SIZES.SMALL]: 16,
  [SIZES.MEDIUM]: 20,
  [SIZES.LARGE]: 24,
};

const ICONS = {
  ADD_TO_CART: 'cart-plus',
  REPLACE: 'replace',
  ADJUST: 'edit',
  INCREASE: 'plus',
  DECREASE: 'minus',
  CANCEL: 'x',
  CONFIRM: 'check',
  DELETE: 'trash'
};

const ProductAddToCart = (props) => {

  const {
    className,

    //
    product = {},
    quantity,

    //
    disabled,
    isAddToCartDisabled,
    isAddToCartLoading,
    shouldReset,
    shouldAdjustQuantity,

    //
    size = SIZES.MEDIUM,
    align = ALIGNMENTS.END,

    //
    isCompact,
    isPrimary,
    isReplace,
    isVerbose,
    isInputRounded,
    withInputFrame,
    withAddToCartIcon,
    withAddToCartText,
    withAdjustIcon,

    //
    onAddToCartClick = () => {},
    onQuantityChange = () => {},

  } = props;

  // Refs

  const inputRef = React.useRef(null);
  const editRef = React.useRef(null);

  // Hooks

  const { t } = useTranslation();
  const { data: config } = useConfig();

  const { errors } = config || {};

  const [editValue, setEditValue] = React.useState(0);
  const [isEditMode, setEditMode] = React.useState(false);

  // On Click Outside Hook

  useOnclickOutside(() => {
    setEditMode(false);
  }, {
    disabled: !isEditMode,
    refs: [editRef]
  });

  // Debounce Hook

  const {
    value: quantityTarget, reset, decrease, increase, update
  } = useDebouncedValue(onQuantityChange, { ms: 600, initialValue: quantity });

  // Props

  const {
    maxAllowedQuantity, maxAvailableQuantity,
  } = product;

  const [maxQuantity, errorMessage] = React.useMemo(() => (
    ProductHelpers.getMaxAllowedQuantity({ maxAllowedQuantity, maxAvailableQuantity }, errors)
  ), [maxAllowedQuantity, maxAvailableQuantity, errors]);

  const targetQuantity = quantity > maxQuantity ? maxQuantity : null;

  const canSaveEditValue = editValue <= maxQuantity;
  const canIncreaseQuantity = quantityTarget < maxQuantity;

  const value = isEditMode
    ? editValue
    : (isVerbose ? t('PRODUCT.QUANTITY_IN_CART', { quantity: quantityTarget }) : quantityTarget);

  // Effects

  React.useEffect(() => {
    if (shouldReset) {
      reset();
    }
  }, [shouldReset]);

  // Handlers

  const handleCloseEditMode = (e) => {
    if (e.relatedTarget && !e.relatedTarget.getAttribute('data-prevent-manual-edit-close')) {
      setEditMode(false);
    }
  };

  // Classes

  const rootClasses = classnames(
    { [styles[align]]: align },
    className
  );

  // --- Add

  const addClasses = classnames(
    { [styles.compact]: isCompact },
    { [styles[size]]: size },
  );

  // --- Adjust

  const adjustClasses = classnames(
    { [styles.compact]: isCompact },
    { [styles[size]]: size }
  );

  // --- Edit

  const editClasses = classnames(
    { [styles.compact]: isCompact },
    { [styles[size]]: size },
  );

  // Render

  return (
    <div className={classnames(styles.root, rootClasses)}>

      {/* Add To Cart */}
      {
        (quantity === 0) && (
          <div className={classnames(styles.add, addClasses)}>
            <AddToCartButton
              size={size}
              isPrimary={isPrimary}
              isCompact={isCompact}
              isReplace={isReplace}
              withText={withAddToCartText}
              withIcon={withAddToCartIcon}
              loading={isAddToCartLoading}
              disabled={disabled || isAddToCartDisabled}
              onClick={(e) => {
                e.preventDefault();
                onAddToCartClick();
              }}
            />
          </div>
        )
      }

      {/* Adjust Quantity */}
      {
        (quantity > 0 && shouldAdjustQuantity && targetQuantity) && (
          <div className={classnames(styles.adjust, adjustClasses)}>
            <div className={classnames(styles.btnWrapper, styles.adjustBtnWrapper)}>
              <AdjustQuantityButton
                targetQuantity={targetQuantity}
                size={size}
                isPrimary={isPrimary}
                isCompact={isCompact}
                withIcon={withAdjustIcon}
                onClick={(e) => {
                  e.preventDefault();
                  onQuantityChange(quantity, targetQuantity);
                }}
              />
            </div>
          </div>
        )
      }

      {/* Edit Quantity */}
      {
        (quantity > 0 && !shouldAdjustQuantity) && (
          <div ref={editRef} className={classnames(styles.edit, editClasses)}>

            {/* Decrease / Remove / Cancel  */}
            <QuantityButton
              className={styles.decreaseBtn}
              size={size}
              quantity={quantityTarget}
              disabled={disabled || (isEditMode ? false : quantityTarget <= 0)}
              isFixed={isVerbose}
              isEditMode={isEditMode}
              icons={[
                { name: ICONS.CANCEL, isVisible: isEditMode, fromTop: true },
                { name: ICONS.DECREASE, isVisible: !isEditMode && quantity > 1 },
                { name: ICONS.DELETE, strokeWidth: 2, isVisible: !isEditMode && (quantity === 0 || quantity === 1) }
              ]}
              onClick={
                !isEditMode
                  ? decrease
                  : () => setEditMode(false)
              }
              onEsc={() => {
                if (isEditMode) {
                  setEditMode(false);
                }
              }}
              onBlur={handleCloseEditMode}
            />

            {/* Input */}
            <QuantityInput
              ref={inputRef}
              size={size}
              value={value}
              disabled={disabled}
              hasError={isEditMode && !canSaveEditValue}
              isVerbose={isVerbose}
              isCompact={isCompact}
              isRounded={isInputRounded}
              withFrame={withInputFrame}
              onChange={(updatedValue) => {
                setEditValue(updatedValue);
              }}
              onEnter={() => {
                if (canSaveEditValue) {
                  update(editValue || 0);
                  setEditMode(false);
                }
              }}
              onEsc={() => {
                setEditMode(false);
              }}
              onFocus={() => {
                if (!isEditMode) {
                  inputRef.current.select();
                  setEditMode(true);
                  setEditValue(quantityTarget);
                }
              }}
              onBlur={handleCloseEditMode}
            />

            {/* Increase / Confirm */}
            <QuantityButton
              className={styles.increaseBtn}
              title={
                isEditMode
                  ? (canSaveEditValue ? null : errorMessage)
                  : (canIncreaseQuantity ? null : errorMessage)
              }
              size={size}
              variant={isEditMode ? 'primary' : 'subtle'}
              quantity={quantityTarget}
              disabled={disabled || (isEditMode ? !canSaveEditValue : !canIncreaseQuantity)}
              isFixed={isVerbose}
              isEditMode={isEditMode}
              icons={[
                { name: ICONS.CONFIRM, isVisible: isEditMode, fromTop: true },
                { name: ICONS.INCREASE, isVisible: !isEditMode }
              ]}
              onClick={
                !isEditMode
                  ? increase
                  : () => {
                    update(editValue || 0);
                    setEditMode(false);
                  }
              }
              onEsc={() => {
                if (isEditMode) {
                  setEditMode(false);
                }
              }}
              onBlur={handleCloseEditMode}
            />
          </div>
        )
      }
    </div>
  );
};

// Components

// --- Add To Cart Button

const AddToCartButton = (props) => {
  const {
    size,
    isPrimary, isReplace,
    withText, withIcon,
    loading, disabled,
    onClick = () => {},
  } = props;

  // Hooks

  const { t } = useTranslation();

  // Props

  const iconSize = ADD_TO_CART_ICON_SIZES[size];

  const icon = (
    <Icon
      name={isReplace ? ICONS.REPLACE : ICONS.ADD_TO_CART}
      size={iconSize}
      strokeWidth={2}
    />
  );

  const label = isReplace
    ? t('PRODUCT.REPLACE')
    : t('PRODUCT.ADD_TO_CART');

  // Classes

  const classes = classnames(
    styles.btn,
    styles.addBtn,
    { [styles.fixed]: !withText },
    { [styles.withText]: withText },
    { [styles[size]]: size }
  );

  // Render

  return (
    <Button
      className={classes}
      contentClassName={classnames(styles.btnContent, styles.addBtnContent)}
      iconWrapperClassName={classnames(styles.btnIcon, styles.addBtnIcon, { [styles.spaced]: withText })}
      variant={isPrimary ? 'primary' : 'subtle'}
      size={size}
      startIcon={withIcon ? icon : null}
      loading={loading}
      disabled={disabled}
      onClick={onClick}
    >
      {withText && (
        <span className={classnames(styles.btnLabel, styles.addBtnLabel)}>
          {label}
        </span>
      )}
    </Button>
  );
};

// --- Adjust Quantity Button

const AdjustQuantityButton = (props) => {
  const {
    targetQuantity,
    size,
    isPrimary, withIcon,
    loading, disabled,
    onClick = () => {},
  } = props;

  // Hooks

  const { t } = useTranslation();

  // Props

  const iconSize = ADD_TO_CART_ICON_SIZES[size];

  const icon = (
    <Icon
      name={ICONS.ADJUST}
      size={iconSize}
      strokeWidth={2}
    />
  );

  const label = `${t('ACTIONS.ADD')} ${targetQuantity}`;

  // Classes

  const classes = classnames(
    styles.btn,
    styles.adjustBtn,
    { [styles[size]]: size }
  );

  // Render

  return (
    <Button
      className={classes}
      iconWrapperClassName={classnames(styles.btnIcon, styles.addBtnIcon, styles.spaced)}
      variant={isPrimary ? 'primary' : 'subtle'}
      size={size}
      startIcon={withIcon ? icon : null}
      loading={loading}
      disabled={disabled}
      onClick={onClick}
    >
      {label}
    </Button>
  );
};

// --- Quantity Button

const QuantityButton = (props) => {
  const {
    className,
    size,
    variant = 'subtle',
    disabled,
    icons = [],
    isFixed,
    isEditMode,
    onClick = () => {},
    onEsc = () => {},
    onBlur = () => {}
  } = props;

  // Props

  const iconSize = QUANTITY_ICON_SIZES[size];

  // Classes

  const classes = classnames(
    styles.btn,
    styles.quantityBtn,
    { [styles.fixed]: isFixed },
    { [styles[size]]: size },
    className
  );

  // Render

  return (
    <Button
      className={classes}
      contentClassName={styles.btnContent}
      variant={variant}
      disabled={disabled}
      size={size}
      onClick={onClick}
      onBlur={onBlur}
      onKeyDown={(e) => {
        if (e.keyCode === KEY_CODES.ESC) {
          onEsc(e);
        }
      }}
      data-prevent-manual-edit-close={isEditMode}
    >

      {/* Icons */}
      {
        !!icons?.length && icons.map((icon, index) => {
          const {
            name, strokeWidth, isVisible, fromTop
          } = icon;
          return (
            <ButtonIcon
              key={`${index}-${name}`}
              isVisible={isVisible}
              name={name}
              size={iconSize}
              strokeWidth={strokeWidth}
              fromTop={fromTop}
            />
          );
        })
      }

    </Button>
  );
};

// --- Quantity Input

const QuantityInput = React.forwardRef((props, ref) => {
  const {
    //
    size,
    //
    isCompact,
    isEditMode,
    isVerbose,
    isRounded,
    withFrame,
    //
    value,
    disabled,
    hasError,
    //
    onChange = () => {},
    onFocus = () => {},
    onBlur = () => {},
    onEnter = () => {},
    onEsc = () => {},
  } = props;

  // Handlers

  // --- KeyDown

  const handleKeyDown = (e) => {
    e.stopPropagation();

    if (e.keyCode === KEY_CODES.ENTER) {
      ref?.current?.blur();
      onEnter();

    } else if (e.keyCode === KEY_CODES.ESC) {
      ref?.current?.blur();
      onEsc();
    }
  };

  // --- Change

  const handleChange = (e) => {
    let newValue = e?.target?.value?.replace(/[^\d+]/, '');

    if (newValue) {
      newValue = parseInt(newValue || 0, 10);
    }

    onChange(newValue);
  };

  // Classes

  const classes = classnames(
    styles.input,
    styles.quantityInput,
    { [styles.compact]: isCompact },
    { [styles.framed]: withFrame },
    { [styles.rounded]: isRounded },
    { [styles.fixed]: !withFrame && !isVerbose },
    { [styles.error]: hasError },
    { [styles[size]]: size }
  );

  // Render

  return (
    <input
      ref={ref}
      className={classes}
      type="text"
      value={value}
      disabled={disabled}
      inputMode="numeric"
      regex="^[0-9]*$"
      maxLength={3}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      data-prevent-manual-edit-close={isEditMode}
    />
  );
});

// --- Button Icon

const ButtonIcon = (props) => {
  const {
    className,
    name,
    size, strokeWidth = 2,
    fromTop, isVisible
  } = props;

  const classes = classnames(
    styles.icon,
    { [styles.visible]: isVisible },
    { [styles.top]: fromTop },
    className
  );

  return (
    <div className={classes}>
      <Icon
        name={name}
        strokeWidth={strokeWidth}
        size={size}
      />
    </div>
  );
};

// Props

ProductAddToCart.propTypes = {
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),

  //
  product: PropTypes.shape({
    maxAvailableQuantity: PropTypes.number,
    maxAllowedQuantity: PropTypes.number
  }),
  quantity: PropTypes.number,

  //
  disabled: PropTypes.bool,
  isAddToCartDisabled: PropTypes.bool,
  isAddToCartLoading: PropTypes.bool,
  shouldReset: PropTypes.bool,
  shouldAdjustQuantity: PropTypes.bool,

  //
  size: PropTypes.oneOf(Object.values(SIZES)),
  align: PropTypes.oneOf(Object.values(ALIGNMENTS)),

  //
  isCompact: PropTypes.bool,
  isPrimary: PropTypes.bool,
  isReplace: PropTypes.bool,
  isVerbose: PropTypes.bool,
  isInputRounded: PropTypes.bool,
  withAddToCartIcon: PropTypes.bool,
  withAdjustIcon: PropTypes.bool,
  withAddToCartText: PropTypes.bool,
  withInputFrame: PropTypes.bool,

  //
  onAddToCartClick: PropTypes.func,
  onQuantityChange: PropTypes.func,
};

// Export

export default ProductAddToCart;
